import React from 'react'
import { Link } from 'react-router'
import cx from 'classnames'
import {
  DataCard,
  DataCardHeader,
  Details,
  Dot,
  Title,
  TimeFrame,
  MessageWithPrefix,
  RedBadge,
  OrangeBadge,
  DarkBlueBadge,
  LightBlueBadge,
  TealBadge,
  YellowBadge,
  GreenBadge,
} from '../data-card/data-card-parts'
import OpenIssueHeader from '../issues/open-issue-header'

import AreaOptionsContainer from '../../containers/areas/area-options-container'

class ServicingCard extends React.Component {
  componentWillUnmount() {
    this.props.removeMergedServicesLabels()
  }

  redoReason () {
    let date = (this.props.redoneAt !== null) ? ` (${this.props.redoneAt.format('hh:mmA')})` : ''
    if (this.props.redoReason && this.props.redoReason.length > 0) {
      return (
        <MessageWithPrefix
          key='redo-reason'
          prefix='Redo reason: '
          text={`${this.props.redoReason}${date}`}
        />
      )
    }
  }

  delayedReason () {
    if (this.props.delayedReason && this.props.delayedReason.length > 0 && this.props.delayedAt && this.props.delayedBy) {
      return (
        <MessageWithPrefix
          key='delayed-reason'
          prefix='Delay: '
          text={`${this.props.delayedReason}, Marked by ${this.props.delayedBy.name} (${this.props.delayedAt.format('hh:mmA')})`}
        />
      )
    }
  }

  assignedMessage (assignee) {
    let date = (this.props.assignedAt !== null) ? ` (${this.props.assignedAt.format('hh:mmA')})` : ''

    return (
      <MessageWithPrefix
        key='assigned-message'
        prefix=''
        text={`Assigned to ${assignee}${date}`}
      />
    )
  }

  claimedMessage (claimer) {
    let date = (this.props.claimedAt !== null) ? ` (${this.props.claimedAt.format('hh:mmA')})` : ''
    return (
      <MessageWithPrefix
        key='claimed-message'
        prefix=''
        text={`Claimed By ${claimer}${date}`}
      />
    )
  }

  noServicesCompletedYet () {
    const { services } = this.props
    if (!services) return true
    return services.every(s => s.completedAt === null)
  }

  allServicesCompleted () {
    const { services } = this.props
    if (!services) return false
    return services.every(s => (s.completedAt !== undefined && s.completedAt !== null))
  }

  addedServices () {
    // only show message if there are some complete and some incomplete services
    if (this.noServicesCompletedYet() || this.allServicesCompleted()) return null

    const addedServices = this.props.services.filter(s => s.completedAt === null)

    return (
      <MessageWithPrefix
        prefix="Additional services required: "
        text={addedServices.map(s => (`${s.name}`)).join(', ')}
      />
    )
  }

  requireImagesBadge = ({ requireImages, beforeAfters, beforeImage, afterImage }) => {
    if (!requireImages && beforeAfters.length == 0 && !beforeImage.url && !afterImage.url) return null

    if (beforeAfters.length == 0) return (<RedBadge text="Requires Images" />)
    if (beforeAfters.filter(({ beforeImage, afterImage}) => beforeImage !== null && afterImage === null).length > 0) return (<YellowBadge text="Requires After Image" />)
    return (<GreenBadge text="Images" />)
  }

  inspectionImagesBadge = ({ inspectionImageA = {}, inspectionImageB = {} }) => {
    if (!inspectionImageA.url && !inspectionImageB.url) return null

    return (<RedBadge text="Inspection Images" />)
  }

  render() {
    let highPriorityBadge = null
    let openBadge = null
    let delayedBadge = null
    let inProgressBadge = null
    let temporarilyOccupiedBadge = null
    let redoneBadge = null
    let mergedServicesNotification = null
    let openIssuesBadge = null
    let assignee = null
    let claimer = null
    const requireImagesBadge = this.requireImagesBadge(this.props)

    if (this.props.highPriority) {
      highPriorityBadge = <RedBadge text='high priority' />
    }

    if (this.props.status === 'open') {
      openBadge = <LightBlueBadge text={this.props.status} />
    }

    if (this.props.status === 'delayed') {
      delayedBadge = <YellowBadge text='Delay'  additionalClasses="black-text" />
    }

    if (this.props.status === 'temporarily_occupied') {
      temporarilyOccupiedBadge = <DarkBlueBadge text='temporarily occupied' />
    }

    if (this.props.redone) {
      redoneBadge = <RedBadge text='redo' />
    }

    if (this.props.status === 'in_progress') {
      let date = (this.props.inProgressAt !== null) ? ` (${this.props.inProgressAt.format('hh:mmA')})` : ''
      inProgressBadge = <OrangeBadge text={`In Progress${date}`} />
    }

    if (this.props.mergedServices && this.props.mergedServices.length > 0) {
      mergedServicesNotification =
        <span className='merged-services-warning'>
          ({this.props.mergedServices.length} Backlog Merges)
        </span>
    }

    if (this.props.assignee && this.props.assignee.name) {
      assignee = this.props.assignee.name
    }

    if (this.props.claimer && this.props.claimer.name) {
      claimer = this.props.claimer.name
    }

    return (
      <DataCard className='separated'>
        {this.props.openIssueCount > 0 &&
          <OpenIssueHeader
            openIssueCount={this.props.openIssueCount}
            areaId={this.props.area.id}
            workOrderId={this.props.workOrderId}
            servicingId={this.props.id}
            locationId={this.props.location}
          />}
        <AreaOptionsContainer
          urlPath={this.props.urlPath}
          servicingId={this.props.id}
          buttonClasses={cx({ 'offset-down': this.props.openIssueCount > 0})}
        />
        <Link to={`/work-orders/${this.props.workOrderId}/details/areas/${this.props.id}`}>
          <Details>
            <Title text={this.props.area.name || ''} />
            <Dot />
            <TimeFrame text={this.props.submittedAt.format('MMMM D, YYYY h:mmA')} />
            <div>
              {delayedBadge}
              {highPriorityBadge}
              {redoneBadge}
              {openBadge}
              {inProgressBadge}
              {temporarilyOccupiedBadge}
              {requireImagesBadge}
              {this.inspectionImagesBadge(this.props)}
              {this.props.labels.map(label => <TealBadge key={label} text={label} />)}
            </div>
          </Details>
          {this.props.showInputted && this.props.inputtedBy && <MessageWithPrefix prefix="Inputted by " text={this.props.inputtedBy.name}/>}
          <MessageWithPrefix
            key={this.props.id}
            prefix={mergedServicesNotification}
            text={
              this.props.serviceNames.join(', ') || 'None'
            }
          />

          {claimer ? this.claimedMessage(claimer) : (assignee ? this.assignedMessage(assignee) : null)}
          {this.addedServices()}
          {this.delayedReason()}
          {this.redoReason()}
        </Link>
      </DataCard>
    )
  }
}

export default ServicingCard



// WEBPACK FOOTER //
// ./src/components/servicings/servicing-card.js